import Vue from 'vue';
import { getLangValue } from '@/tools/languageValue';
import { PLAYER_STATE } from '@/tools/consts';
import { uiComponents, uiStates } from '@/tools/viewState';
import { mutationTypes, actionTypes, errorTypes, getterTypes } from '@/types';
import { nativeMethodCall } from '@/tools/nativeInterface';
import * as mediaSyncronization from '@/tools/mediaSyncronization';

export default {
	state: {
		mediaID: null,
		loadedMediaObject: null,
		source: null,
		title: '',
		attribution: '',
		description: '',
		duration: 0.0,
		currentTime: 0.0,
		playerState: PLAYER_STATE.emtpy,
		isSynced: false,
		isFullscreen: false,
		videoPlayer: null,
		queue: null,
	},
	getters: {
		[getterTypes.videoSource](state) {
			return state.source;
		},
		[getterTypes.videoIsActive](state) {
			return (
				state.playerState !== PLAYER_STATE.stopped &&
				state.playerState !== PLAYER_STATE.paused &&
				state.playerState !== PLAYER_STATE.emtpy
			);
		},
	},
	mutations: {
		[mutationTypes.videoSetSource](state, { mediaID, mediaObject, startPlaying, isSynced }) {
			state.mediaID = mediaID;
			state.loadedMediaObject = mediaObject;

			state.source = mediaObject.Sources ? getLangValue(mediaObject.Sources) : null;
			state.title = mediaObject.Title ? getLangValue(mediaObject.Title) : '';
			state.attribution = mediaObject.Attribution ? getLangValue(mediaObject.Attribution) : '';
			state.description = mediaObject.Description ? getLangValue(mediaObject.Description) : '';
			state.duration = mediaObject.Duration ? Number(getLangValue(mediaObject.Duration)) : 0;
			state.isSynced = isSynced || false;
			state.open = true;
			if (startPlaying) {
				state.playerState = PLAYER_STATE.playing;
			}
		},
		[mutationTypes.videoSetState](state, { playerState }) {
			state.playerState = playerState;
		},
		[mutationTypes.videoSetCurrentTime](state, { currentTime }) {
			state.currentTime = currentTime;
		},
		[mutationTypes.videoStart](state) {
			state.playerState = PLAYER_STATE.playing;
		},
		[mutationTypes.videoPause](state) {
			state.playerState = PLAYER_STATE.paused;
		},
		[mutationTypes.videoStop](state) {
			state.playerState = PLAYER_STATE.stopped;
		},
		[mutationTypes.unSetSource](state) {
			state.mediaID = null;
			state.loadedMediaObject = null;
			state.source = null;
			state.title = '';
			state.description = '';
			state.duration = 0;
			state.playerState = PLAYER_STATE.emtpy;
		},
		[mutationTypes._videoToggle](state) {
			let result = PLAYER_STATE.emtpy;
			switch (state.playerState) {
				case PLAYER_STATE.stopped:
				case PLAYER_STATE.paused:
					result = PLAYER_STATE.playing;
					break;
				case PLAYER_STATE.playing:
				case PLAYER_STATE.loading:
				case PLAYER_STATE.syncing:
					result = PLAYER_STATE.paused;
			}
			state.playerState = result;
		},
		[mutationTypes.videoSetFullScreen](state, { isFullscreen }) {
			state.isFullscreen = isFullscreen;
		},
		[mutationTypes.videoSetPlayer](state, player) {
			state.videoPlayer = player;
		},
		[mutationTypes.setVideoQueue](state, queue) {
			state.queue = queue;
		},
	},
	actions: {
		[actionTypes.videoToggle]({ state, rootState, commit }) {
			if (rootState.isApp) {
				nativeMethodCall({ method: 'nativeAppPreventSleep', params: [true] });
			}
			if (state.isSynced) {
				mediaSyncronization.onClickTogglePlay();
			} else {
				commit(mutationTypes._videoToggle);
			}
		},
		[actionTypes.videoPlayQueued]({ state, dispatch, commit }) {
			if (state.queue) {
				dispatch(actionTypes.videoLoadAndPlay, state.queue);
				commit(mutationTypes.setVideoQueue, null);
			}
		},
		[actionTypes.videoLoadAndPlay](
			{ commit, dispatch, rootState, state },
			{ mediaID, isSynced, queueVideo, mediaDuration }
		) {
			// TODO Error handling
			const mediaObject = rootState.currentMediaObjectMap.get(mediaID);

			if (!mediaObject) {
				if (queueVideo) {
					commit(mutationTypes.setVideoQueue, { mediaID, isSynced, mediaDuration });
					return;
				}
				dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.unknownMediaObject) });
				return;
			}
			// if Audio is Playing stop it
			if (rootState.playerAudio.playerState == PLAYER_STATE.playing) {
				dispatch(actionTypes.audioTogglePlay);
			}

			// If the video is already playing stop it first so we can
			// exchange the source and start a new video.
			if (state.playerState === PLAYER_STATE.playing && !isSynced) {
				Vue.nextTick(() => {
					commit(mutationTypes.videoStop);
					Vue.nextTick(() => {
						commit(mutationTypes.videoStart);
					});
				});
			}
			if (isSynced) {
				// The synced video is always playing, set the state to playing.
				commit(mutationTypes.videoStart);
				mediaSyncronization.initializeSyncronization({
					$media: state.videoPlayer,
					mediaDuration:
						mediaDuration || mediaObject.Duration ? Number(getLangValue(mediaObject.Duration)) : 0,
					startPlaying: true,
					dataSourceURL: rootState.dataSourceURL,
					src: getLangValue(mediaObject.Sources),
					mediaType: 'video',
					commit,
				});
			}
			// The video was synced before we now need to unbind the sync function
			else if (state.isSynced) {
				mediaSyncronization.unSync();
			}
			commit(mutationTypes.videoSetSource, {
				mediaObject,
				mediaID,
				startPlaying: !isSynced,
				isSynced,
			});
			commit(mutationTypes.viewStateSetUiState, {
				id: uiComponents.uiVideo,
				uiState: uiStates.maximised,
			});
			setTimeout(() => commit(mutationTypes.videoStart), 0);
		},
	},
	plugins: [],
};
